import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  PhotoCamera,
  Refresh,
  Info,
  CheckCircle,
  Error,
  Person,
  Phone,
  Email,
  CameraAlt
} from '@mui/icons-material';
import { Camera } from 'react-camera-pro';
import reservationService from '../services/reservationService';

// Simple validation functions (inline to avoid import issues)
const validateCPF = (cpf) => {
  if (!cpf) return false;
  cpf = cpf.replace(/[^\d]/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let remainder = 11 - (sum % 11);
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(9))) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  remainder = 11 - (sum % 11);
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(10))) return false;
  
  return true;
};

const validatePassport = (passport) => {
  if (!passport) return false;
  const passportRegex = /^[A-Z0-9]{6,9}$/i;
  return passportRegex.test(passport.replace(/\s/g, ''));
};

const validateEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  if (!phone) return false;
  const cleanPhone = phone.replace(/[^\d+]/g, '');
  const phoneRegex = /^\+\d{10,15}$/;
  return phoneRegex.test(cleanPhone);
};

// Temperature conversion utilities
const celsiusToFahrenheit = (celsius) => {
  return Math.round((celsius * 9/5) + 32);
};

const fahrenheitToCelsius = (fahrenheit) => {
  return Math.round((fahrenheit - 32) * 5/9);
};

const isValidTemperature = (temp, unit = 'C') => {
  if (unit === 'C') {
    return temp >= 16 && temp <= 30;
  } else {
    return temp >= 61 && temp <= 86; // 16-30°C in Fahrenheit
  }
};

// Complete translations for EN, PT, ES
const translations = {
  en: {
    guest_information_title: 'Guest Information Collection',
    privacy_agreement: 'Privacy Agreement',
    privacy_text_1: 'We collect your personal information to ensure a safe and secure stay. Your data will be used for identification purposes and to comply with local regulations.',
    privacy_text_2: 'Your information will be stored securely and will not be shared with third parties except as required by law or for security purposes.',
    privacy_text_3: 'By proceeding, you consent to the collection and processing of your personal data as described above.',
    privacy_accept: 'I agree to the privacy terms and consent to data processing',
    continue: 'Continue',
    guest_information: 'Guest Information',
    document_verification: 'Document Verification',
    photo_capture: 'Photo Capture',
    review_submit: 'Review & Submit',
    loading_reservation: 'Loading reservation...',
    first_name: 'First Name',
    last_name: 'Last Name',
    email: 'Email',
    phone_with_country_code: 'Phone (with country code)',
    document_type: 'Document Type',
    passport_number: 'Passport Number',
    take_photo: 'Take Photo',
    capture: 'Capture',
    cancel: 'Cancel',
    next_guest: 'Next Guest',
    previous_guest: 'Previous Guest',
    guest: 'Guest',
    of: 'of',
    check_in: 'Check-in',
    check_out: 'Check-out',
    guests: 'Guests',
    adults: 'adults',
    nights: 'nights',
    camera_permission_denied: 'Camera permission denied. Please allow camera access.',
    photo_captured_successfully: 'Photo captured successfully!',
    retake_photo: 'Retake Photo',
    information_already_provided: 'Information Already Provided',
    contact_host_message: 'Please contact your host if you need to make changes.',
    reservation_not_found: 'Reservation Not Found',
    check_reservation_id: 'Please check your reservation ID.',
    save_continue_later: 'Save and Continue Later',
    preferences: 'Preferences',
    aromatherapy: 'Aromatherapy',
    aromatherapy_question: 'Would you like us to turn on aromatherapy?',
    air_conditioning: 'Air Conditioning',
    ac_question: 'Would you like us to turn on the AC before your arrival?',
    temperature: 'Temperature',
    temperature_f: 'Temperature (°F)',
    temperature_c: 'Temperature (°C)',
    yes: 'Yes',
    no: 'No',
    save_progress: 'Save Progress',
    continue_later: 'Continue Later',
    submit_all: 'Submit All',
    optional: '(Optional)',
    pending_guests: 'pending guests',
    pending_guest: 'pending guest',
    progress_saved: 'Progress Saved!',
    share_link_message: 'You can now send the link to other guests to complete their information.',
    copy_link: 'Copy Link',
    return_to_form: 'Return to Form',
    link_copied: 'Link copied to clipboard!'
  },
  pt: {
    guest_information_title: 'Coleta de Informações dos Hóspedes',
    privacy_agreement: 'Acordo de Privacidade',
    privacy_text_1: 'Coletamos suas informações pessoais para garantir uma estadia segura. Seus dados serão usados para fins de identificação e para cumprir regulamentações locais.',
    privacy_text_2: 'Suas informações serão armazenadas com segurança e não serão compartilhadas com terceiros, exceto conforme exigido por lei ou para fins de segurança.',
    privacy_text_3: 'Ao prosseguir, você consente com a coleta e processamento de seus dados pessoais conforme descrito acima.',
    privacy_accept: 'Concordo com os termos de privacidade e consinto com o processamento de dados',
    continue: 'Continuar',
    guest_information: 'Informações do Hóspede',
    document_verification: 'Verificação de Documentos',
    photo_capture: 'Captura de Foto',
    review_submit: 'Revisar e Enviar',
    loading_reservation: 'Carregando reserva...',
    first_name: 'Nome',
    last_name: 'Sobrenome',
    email: 'Email',
    phone_with_country_code: 'Telefone (com código do país)',
    document_type: 'Tipo de Documento',
    passport_number: 'Número do Passaporte',
    take_photo: 'Tirar Foto',
    capture: 'Capturar',
    cancel: 'Cancelar',
    next_guest: 'Próximo Hóspede',
    previous_guest: 'Hóspede Anterior',
    guest: 'Hóspede',
    of: 'de',
    check_in: 'Check-in',
    check_out: 'Check-out',
    guests: 'Hóspedes',
    adults: 'adultos',
    nights: 'noites',
    camera_permission_denied: 'Permissão da câmera negada. Permita o acesso à câmera.',
    photo_captured_successfully: 'Foto capturada com sucesso!',
    retake_photo: 'Tirar Nova Foto',
    information_already_provided: 'Informações Já Fornecidas',
    contact_host_message: 'Entre em contato com seu anfitrião se precisar fazer alterações.',
    reservation_not_found: 'Reserva Não Encontrada',
    check_reservation_id: 'Verifique seu ID de reserva.',
    save_continue_later: 'Salvar e Continuar Depois',
    preferences: 'Preferências',
    aromatherapy: 'Aromaterapia',
    aromatherapy_question: 'Gostaria que ligássemos a aromaterapia?',
    air_conditioning: 'Ar Condicionado',
    ac_question: 'Gostaria que ligássemos o ar condicionado antes da sua chegada?',
    temperature: 'Temperatura',
    temperature_f: 'Temperatura (°F)',
    temperature_c: 'Temperatura (°C)',
    yes: 'Sim',
    no: 'Não',
    save_progress: 'Salvar Progresso',
    continue_later: 'Continuar Depois',
    submit_all: 'Enviar Tudo',
    optional: '(Opcional)',
    pending_guests: 'hóspedes pendentes',
    pending_guest: 'hóspede pendente',
    progress_saved: 'Progresso Salvo!',
    share_link_message: 'Agora você pode enviar o link para outros hóspedes completarem suas informações.',
    copy_link: 'Copiar Link',
    return_to_form: 'Voltar ao Formulário',
    link_copied: 'Link copiado para a área de transferência!'
  },
  es: {
    guest_information_title: 'Recolección de Información de Huéspedes',
    privacy_agreement: 'Acuerdo de Privacidad',
    privacy_text_1: 'Recopilamos su información personal para garantizar una estancia segura. Sus datos se utilizarán para fines de identificación y para cumplir con las regulaciones locales.',
    privacy_text_2: 'Su información se almacenará de forma segura y no se compartirá con terceros, excepto según lo requiera la ley o para fines de seguridad.',
    privacy_text_3: 'Al continuar, usted consiente la recopilación y procesamiento de sus datos personales como se describe arriba.',
    privacy_accept: 'Acepto los términos de privacidad y consiento el procesamiento de datos',
    continue: 'Continuar',
    guest_information: 'Información del Huésped',
    document_verification: 'Verificación de Documentos',
    photo_capture: 'Captura de Foto',
    review_submit: 'Revisar y Enviar',
    loading_reservation: 'Cargando reserva...',
    first_name: 'Nombre',
    last_name: 'Apellido',
    email: 'Email',
    phone_with_country_code: 'Teléfono (con código de país)',
    document_type: 'Tipo de Documento',
    passport_number: 'Número de Pasaporte',
    take_photo: 'Tomar Foto',
    capture: 'Capturar',
    cancel: 'Cancelar',
    next_guest: 'Siguiente Huésped',
    previous_guest: 'Huésped Anterior',
    guest: 'Huésped',
    of: 'de',
    check_in: 'Check-in',
    check_out: 'Check-out',
    guests: 'Huéspedes',
    adults: 'adultos',
    nights: 'noches',
    camera_permission_denied: 'Permiso de cámara denegado. Permita el acceso a la cámara.',
    photo_captured_successfully: '¡Foto capturada exitosamente!',
    retake_photo: 'Tomar Nueva Foto',
    information_already_provided: 'Información Ya Proporcionada',
    contact_host_message: 'Contacte a su anfitrión si necesita hacer cambios.',
    reservation_not_found: 'Reserva No Encontrada',
    check_reservation_id: 'Verifique su ID de reserva.',
    save_continue_later: 'Guardar y Continuar Después',
    preferences: 'Preferencias',
    aromatherapy: 'Aromaterapia',
    aromatherapy_question: '¿Le gustaría que activemos la aromaterapia?',
    air_conditioning: 'Aire Acondicionado',
    ac_question: '¿Le gustaría que encendamos el aire acondicionado antes de su llegada?',
    temperature: 'Temperatura',
    temperature_f: 'Temperatura (°F)',
    temperature_c: 'Temperatura (°C)',
    yes: 'Sí',
    no: 'No',
    save_progress: 'Guardar Progreso',
    continue_later: 'Continuar Después',
    submit_all: 'Enviar Todo',
    optional: '(Opcional)',
    pending_guests: 'huéspedes pendientes',
    pending_guest: 'huésped pendiente',
    progress_saved: '¡Progreso Guardado!',
    share_link_message: 'Ahora puede enviar el enlace a otros huéspedes para completar su información.',
    copy_link: 'Copiar Enlace',
    return_to_form: 'Volver al Formulario',
    link_copied: '¡Enlace copiado al portapapeles!'
  }
};

const GuestInformationFinal = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const cameraRef = useRef(null);
  
  // State management
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState('pt');
  const [currentStep, setCurrentStep] = useState(0);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [guests, setGuests] = useState([]);
  const [currentGuestIndex, setCurrentGuestIndex] = useState(0);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSaveSuccessDialog, setShowSaveSuccessDialog] = useState(false);
  const [preferences, setPreferences] = useState({
    aromatherapy: true, // Default YES for aromatherapy
    airConditioning: false, // Default NO for AC
    temperature: 22 // Default 22°C (72°F)
  });

  // Load reservation data
  useEffect(() => {
    loadReservation();
  }, [reservationId]);

  const loadReservation = async () => {
    try {
      setLoading(true);
      const data = await reservationService.getReservation(reservationId);
      
      if (data.status === 'Guest Information Provided') {
        setError('Information already provided');
        return;
      }
      
      setReservation(data);
      
      // Parse the nested rawData if it exists (DynamoDB stores complex data as JSON strings)
      let parsedData = data;
      if (data.rawData && typeof data.rawData === 'string') {
        try {
          parsedData = JSON.parse(data.rawData);
          console.log('Parsed rawData:', parsedData);
        } catch (e) {
          console.warn('Could not parse rawData:', e);
          parsedData = data;
        }
      }
      
      // Fix: Use numberOfGuests instead of numberOfAdults (API returns numberOfGuests)
      const numberOfAdults = parseInt(parsedData.numberOfGuests) || parseInt(parsedData.numberOfAdults) || parseInt(data.numberOfGuests) || parseInt(data.numberOfAdults) || 1;
      
      // Check if there's existing partial data to load
      let initialGuests;
      if (parsedData.partialGuestInformation && parsedData.partialGuestInformation.length > 0) {
        // Load existing partial data
        console.log('Loading existing partial data:', parsedData.partialGuestInformation);
        initialGuests = parsedData.partialGuestInformation.map((guest, index) => {
          // Check if guest has an existing photo (could be base64, S3 URL, or blob)
          const hasExistingPhoto = guest.photo && (
            typeof guest.photo === 'string' || // base64 or S3 URL
            (guest.photo && typeof guest.photo === 'object') // blob object
          );
          
          return {
            id: index + 1,
            firstName: guest.firstName || '',
            lastName: guest.lastName || '',
            email: guest.email || '',
            phone: guest.phone || '',
            documentType: guest.documentType || '',
            documentNumber: guest.documentNumber || '',
            photo: guest.photo || null,
            photoValidated: hasExistingPhoto || guest.photoValidated || false,
            completed: guest.completed || false,
            // Add flag to indicate this is a loaded photo (not newly captured)
            photoSource: hasExistingPhoto ? 'loaded' : null
          };
        });
        
        // Fill remaining slots if needed
        while (initialGuests.length < numberOfAdults) {
          initialGuests.push({
            id: initialGuests.length + 1,
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            documentType: '',
            documentNumber: '',
            photo: null,
            photoValidated: false,
            completed: false
          });
        }
        
        // Load existing preferences if available
        if (parsedData.preferences) {
          console.log('Loading existing preferences:', parsedData.preferences);
          setPreferences({
            aromatherapy: parsedData.preferences.aromatherapy !== undefined ? parsedData.preferences.aromatherapy : true,
            airConditioning: parsedData.preferences.airConditioning !== undefined ? parsedData.preferences.airConditioning : false,
            temperature: parsedData.preferences.temperature || 22
          });
        }
        
        console.log('✅ Loaded partial data successfully:', initialGuests);
      } else {
        // Create fresh guest array
        console.log('No partial data found, creating fresh guest array');
        initialGuests = Array.from({ length: numberOfAdults }, (_, index) => ({
          id: index + 1,
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          documentType: '',
          documentNumber: '',
          photo: null,
          photoValidated: false,
          completed: false
        }));
      }
      
      setGuests(initialGuests);
      setCurrentStep(0); // Start with privacy agreement
    } catch (err) {
      console.error('Error loading reservation:', err);
      setError('Reservation not found');
    } finally {
      setLoading(false);
    }
  };

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const updateGuestField = (field, value) => {
    const updatedGuests = [...guests];
    updatedGuests[currentGuestIndex] = {
      ...updatedGuests[currentGuestIndex],
      [field]: value
    };
    
    if (field === 'phone') {
      const country = value.startsWith('+55') ? 'BR' : 'INT';
      if (country === 'BR') {
        updatedGuests[currentGuestIndex].documentType = 'cpf';
      } else {
        updatedGuests[currentGuestIndex].documentType = 'passport';
      }
    }
    
    setGuests(updatedGuests);
  };

  const handleTakePhoto = () => {
    if (cameraRef.current) {
      try {
        const photo = cameraRef.current.takePhoto();
        console.log('Photo captured:', photo);
        setCapturedImage(photo);
        
        // Convert to blob for storage
        fetch(photo)
          .then(res => {
            if (!res.ok) {
              throw new Error('Failed to fetch photo data');
            }
            return res.blob();
          })
          .then(blob => {
            console.log('Photo converted to blob:', blob);
            
            // Validate blob
            if (!blob || blob.size === 0) {
              throw new Error('Invalid or empty blob created');
            }
            
            const updatedGuests = [...guests];
            updatedGuests[currentGuestIndex] = {
              ...updatedGuests[currentGuestIndex],
              photo: blob,
              photoValidated: true,
              photoSource: 'captured' // Mark as newly captured
            };
            setGuests(updatedGuests);
            setShowCamera(false);
            setError('');
            console.log('Photo saved successfully for guest', currentGuestIndex + 1);
          })
          .catch(error => {
            console.error('Error processing photo:', error);
            setError('Error processing photo. Please try again.');
            
            // Fallback: store the data URL directly
            const updatedGuests = [...guests];
            updatedGuests[currentGuestIndex] = {
              ...updatedGuests[currentGuestIndex],
              photo: photo, // Store data URL directly as fallback
              photoValidated: true,
              photoType: 'dataURL', // Flag to indicate this is a data URL, not blob
              photoSource: 'captured' // Mark as newly captured
            };
            setGuests(updatedGuests);
            setShowCamera(false);
            console.log('Photo saved as data URL fallback');
          });
      } catch (error) {
        console.error('Error taking photo:', error);
        setError('Error taking photo. Please try again.');
      }
    }
  };

  const validateCurrentGuest = () => {
    const guest = guests[currentGuestIndex];
    const errors = [];

    if (!guest.firstName.trim()) errors.push('First name required');
    if (!guest.lastName.trim()) errors.push('Last name required');
    if (!validateEmail(guest.email)) errors.push('Valid email required');
    if (!validatePhone(guest.phone)) errors.push('Valid phone required');
    
    if (guest.documentType === 'cpf' && !validateCPF(guest.documentNumber)) {
      errors.push('Valid CPF required');
    } else if (guest.documentType === 'passport' && !validatePassport(guest.documentNumber)) {
      errors.push('Valid passport required');
    }
    
    if (!guest.photo || !guest.photoValidated) {
      errors.push('Photo required');
    }

    return errors;
  };

  // Helper function to validate and complete all guests
  const validateAndCompleteAllGuests = () => {
    const updatedGuests = [...guests];
    const allErrors = [];

    // Validate each guest
    for (let i = 0; i < updatedGuests.length; i++) {
      const guest = updatedGuests[i];
      const guestErrors = [];

      if (!guest.firstName.trim()) guestErrors.push('First name required');
      if (!guest.lastName.trim()) guestErrors.push('Last name required');
      if (!validateEmail(guest.email)) guestErrors.push('Valid email required');
      if (!validatePhone(guest.phone)) guestErrors.push('Valid phone required');
      
      if (guest.documentType === 'cpf' && !validateCPF(guest.documentNumber)) {
        guestErrors.push('Valid CPF required');
      } else if (guest.documentType === 'passport' && !validatePassport(guest.documentNumber)) {
        guestErrors.push('Valid passport required');
      }
      
      if (!guest.photo || !guest.photoValidated) {
        guestErrors.push('Photo required');
      }

      if (guestErrors.length > 0) {
        allErrors.push(`Guest ${i + 1}: ${guestErrors.join(', ')}`);
      } else {
        // Mark guest as completed if all validations pass
        updatedGuests[i].completed = true;
      }
    }

    return { updatedGuests, errors: allErrors };
  };

  const handleNextGuest = () => {
    const errors = validateCurrentGuest();
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    const updatedGuests = [...guests];
    updatedGuests[currentGuestIndex].completed = true;
    setGuests(updatedGuests);

    if (currentGuestIndex < guests.length - 1) {
      setCurrentGuestIndex(currentGuestIndex + 1);
    } else {
      setCurrentStep(4); // Review step
    }
    
    setError('');
  };

  // Helper function to copy link to clipboard
  const copyLinkToClipboard = async () => {
    const link = `${window.location.origin}/guest-info/${reservationId}`;
    try {
      await navigator.clipboard.writeText(link);
      // Show temporary feedback
      const copyButton = document.querySelector('[data-copy-button="true"]');
      if (copyButton) {
        const originalText = copyButton.textContent;
        copyButton.textContent = t('link_copied');
        setTimeout(() => {
          copyButton.textContent = originalText;
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  // Partial save functionality
  const handlePartialSave = async () => {
    try {
      setSaving(true);
      setError(''); // Clear any existing errors
      
      console.log('Starting partial save...', { guests, preferences });
      
      // Prepare partial data with current state
      const partialData = {
        guestInformation: await Promise.all(
          guests.map(async (guest) => ({
            ...guest,
            // Convert photo blob to base64 if it exists
            photo: guest.photo ? await blobToBase64(guest.photo) : null
          }))
        ),
        preferences: preferences,
        partiallyCompleted: true,
        lastUpdated: new Date().toISOString()
      };

      console.log('Prepared partial data:', partialData);

      const result = await reservationService.savePartialGuestInformation(reservationId, partialData);
      console.log('Partial save result:', result);
      
      // Show success dialog instead of just button feedback
      setShowSaveSuccessDialog(true);
      
      console.log('Partial save successful');
      
    } catch (err) {
      console.error('Error saving partial data:', err);
      setError(`Error saving progress: ${err.message || 'Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  // Helper function to convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      // Handle null, undefined, or empty blob
      if (!blob) {
        console.log('No blob provided, returning null');
        resolve(null);
        return;
      }

      // Check if it's already a string (base64 or data URL)
      if (typeof blob === 'string') {
        console.log('Photo is already a string (data URL), returning as-is');
        resolve(blob);
        return;
      }

      // Check if it's a proper Blob or File object
      if (!(blob instanceof Blob) && !(blob instanceof File)) {
        console.warn('Invalid blob object, attempting to convert:', typeof blob, blob);
        
        // Try to handle object that might have blob-like properties
        if (blob && typeof blob === 'object' && blob.size !== undefined) {
          console.log('Attempting to handle blob-like object');
          resolve(null); // Skip problematic objects
          return;
        }
        
        resolve(null);
        return;
      }

      // Check if blob is empty
      if (blob.size === 0) {
        console.warn('Empty blob provided');
        resolve(null);
        return;
      }

      try {
        const reader = new FileReader();
        
        reader.onload = () => {
          console.log('Successfully converted blob to base64, size:', blob.size);
          resolve(reader.result);
        };
        
        reader.onerror = (error) => {
          console.error('FileReader error:', error);
          reject(new Error('Failed to read blob as base64'));
        };
        
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Error in blobToBase64:', error);
        reject(error);
      }
    });
  };

  // Final submission
  const handleFinalSubmit = async () => {
    try {
      setSaving(true);
      setError(''); // Clear any existing errors
      
      console.log('Starting final submission...', { guests, preferences });
      
      // Validate and complete all guests
      const { updatedGuests, errors } = validateAndCompleteAllGuests();
      
      if (errors.length > 0) {
        setError(`Please complete the following:\n${errors.join('\n')}`);
        setSaving(false);
        return;
      }
      
      // Update the guests state with completed status
      setGuests(updatedGuests);

      // Prepare final data with photo conversion
      console.log('Converting photos to base64...');
      const finalGuestData = await Promise.all(
        updatedGuests.map(async (guest, index) => {
          try {
            console.log(`Processing guest ${index + 1} photo:`, guest.photo);
            
            let convertedPhoto = null;
            if (guest.photo) {
              convertedPhoto = await blobToBase64(guest.photo);
              console.log(`Guest ${index + 1} photo converted successfully`);
            } else {
              console.log(`Guest ${index + 1} has no photo`);
            }

            return {
              ...guest,
              photo: convertedPhoto
            };
          } catch (photoError) {
            console.error(`Error converting photo for guest ${index + 1}:`, photoError);
            // Continue without photo rather than failing entire submission
            return {
              ...guest,
              photo: null,
              photoError: photoError.message
            };
          }
        })
      );

      const finalData = {
        guestInformation: finalGuestData,
        preferences: preferences,
        status: 'Guest Information Provided',
        completedAt: new Date().toISOString()
      };

      console.log('Submitting final data:', finalData);
      
      try {
        const result = await reservationService.submitGuestInformation(reservationId, finalData);
        console.log('Final submission result:', result);
        
        // Redirect to success page
        navigate(`/guest-info-success/${reservationId}`);
      } catch (submitError) {
        console.error('Error during submission:', submitError);
        
        // If submission fails, try without photos as fallback
        if (finalData.guestInformation.some(guest => guest.photo)) {
          console.log('Retrying submission without photos...');
          const fallbackData = {
            ...finalData,
            guestInformation: finalData.guestInformation.map(guest => ({
              ...guest,
              photo: null,
              photoNote: 'Photo submission failed - contact support if needed'
            }))
          };
          
          try {
            const fallbackResult = await reservationService.submitGuestInformation(reservationId, fallbackData);
            console.log('Fallback submission successful:', fallbackResult);
            
            // Show warning but proceed to success
            alert('Information submitted successfully! Note: Photos could not be uploaded - please contact support if needed.');
            navigate(`/guest-info-success/${reservationId}`);
          } catch (fallbackError) {
            console.error('Fallback submission also failed:', fallbackError);
            throw fallbackError;
          }
        } else {
          throw submitError;
        }
      }
      
    } catch (err) {
      console.error('Error submitting guest information:', err);
      setError(`Error submitting information: ${err.message || 'Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography variant="h6" align="center" sx={{ mt: 2 }}>
          {t('loading_reservation')}
        </Typography>
      </Container>
    );
  }

  if (error === 'Information already provided') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Information Already Provided
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please contact your host if you need to make changes.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error === 'Reservation not found') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Error color="error" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Reservation Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please check your reservation ID.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Language Selector */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <FormControl size="small">
          <Select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <MenuItem value="en">🇺🇸 English</MenuItem>
            <MenuItem value="pt">🇧🇷 Português</MenuItem>
            <MenuItem value="es">🇪🇸 Español</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          {t('guest_information_title')}
        </Typography>
        
        {reservation && (
          <Card sx={{ mb: 4, bgcolor: 'primary.50' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {reservation.listingTitle}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('check_in')}: {reservation.checkInDate}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('check_out')}: {reservation.checkOutDate}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('guests')}: {reservation.numberOfGuests || reservation.numberOfAdults} {t('adults')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Privacy Agreement Step */}
        {currentStep === 0 && (
          <Box>
            <Typography variant="h5" gutterBottom align="center">
              {t('privacy_agreement')}
            </Typography>
            
            <Card sx={{ mb: 4, p: 3 }}>
              <Typography variant="body1" paragraph>
                {t('privacy_text_1')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy_text_2')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy_text_3')}
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('privacy_accept')}
                sx={{ mt: 2 }}
              />
            </Card>
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                disabled={!privacyAccepted}
                onClick={() => setCurrentStep(1)}
                sx={{ minWidth: 200 }}
              >
                {t('continue')}
              </Button>
            </Box>
          </Box>
        )}

        {/* Guest Information Steps */}
        {currentStep >= 1 && (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('guest')} {currentGuestIndex + 1} {t('of')} {guests.length}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {guests.map((guest, index) => {
                  // Check if guest has required fields filled
                  const hasBasicInfo = guest.firstName && guest.lastName && guest.email && guest.phone;
                  const hasValidDocument = (guest.documentType === 'cpf' && validateCPF(guest.documentNumber)) || 
                                         (guest.documentType === 'passport' && validatePassport(guest.documentNumber));
                  const hasPhoto = guest.photo && guest.photoValidated;
                  const isComplete = hasBasicInfo && hasValidDocument && hasPhoto;
                  
                  let chipColor = 'default';
                  let chipIcon = <Person />;
                  
                  if (guest.completed || isComplete) {
                    chipColor = 'success';
                    chipIcon = <CheckCircle />;
                  } else if (index === currentGuestIndex) {
                    chipColor = 'primary';
                    chipIcon = <Person />;
                  } else if (hasBasicInfo) {
                    chipColor = 'warning';
                    chipIcon = <Person />;
                  }
                  
                  return (
                    <Chip
                      key={index}
                      label={`${t('guest')} ${index + 1}`}
                      color={chipColor}
                      icon={chipIcon}
                      onClick={() => setCurrentGuestIndex(index)}
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('first_name')}
                  value={guests[currentGuestIndex]?.firstName || ''}
                  onChange={(e) => updateGuestField('firstName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('last_name')}
                  value={guests[currentGuestIndex]?.lastName || ''}
                  onChange={(e) => updateGuestField('lastName', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('email')}
                  type="email"
                  value={guests[currentGuestIndex]?.email || ''}
                  onChange={(e) => updateGuestField('email', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('phone_with_country_code')}
                  placeholder="+55 11 99999-9999"
                  value={guests[currentGuestIndex]?.phone || ''}
                  onChange={(e) => updateGuestField('phone', e.target.value)}
                  required
                />
              </Grid>
              
              {/* Fixed Dropdown with ChatGPT's solution */}
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel id="doc-type-label">
                    {t('document_type')}
                  </InputLabel>
                  <Select
                    labelId="doc-type-label"
                    id="doc-type"
                    value={guests[currentGuestIndex]?.documentType || ''}
                    onChange={(e) => updateGuestField('documentType', e.target.value)}
                    label={t('document_type')}
                  >
                    <MenuItem value="cpf">CPF (Brazilian)</MenuItem>
                    <MenuItem value="passport">Passport (International)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={guests[currentGuestIndex]?.documentType === 'cpf' ? 'CPF' : t('passport_number')}
                  value={guests[currentGuestIndex]?.documentNumber || ''}
                  onChange={(e) => updateGuestField('documentNumber', e.target.value)}
                  placeholder={guests[currentGuestIndex]?.documentType === 'cpf' ? '000.000.000-00' : 'AB1234567'}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Photo Verification
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Please take a clear photo of your face.
                </Typography>
                
                {!guests[currentGuestIndex]?.photo ? (
                  <Button
                    variant="contained"
                    onClick={() => setShowCamera(true)}
                    startIcon={<PhotoCamera />}
                    fullWidth
                  >
                    {t('take_photo')}
                  </Button>
                ) : (
                  <Box>
                    <Alert 
                      severity="success" 
                      sx={{ mb: 2 }}
                      icon={guests[currentGuestIndex]?.photoSource === 'loaded' ? <CheckCircle /> : <PhotoCamera />}
                    >
                      {guests[currentGuestIndex]?.photoSource === 'loaded' 
                        ? 'Photo already uploaded ✓' 
                        : t('photo_captured_successfully')
                      }
                      {/* Debug info */}
                      <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.7 }}>
                        {guests[currentGuestIndex]?.photoSource === 'loaded' ? (
                          <>
                            Source: Previously uploaded
                            {typeof guests[currentGuestIndex]?.photo === 'string' && 
                             guests[currentGuestIndex]?.photo.startsWith('data:') && 
                             `, Size: ${Math.round(guests[currentGuestIndex]?.photo.length * 0.75)} bytes`
                            }
                          </>
                        ) : (
                          <>
                            Type: {typeof guests[currentGuestIndex]?.photo}, 
                            Size: {guests[currentGuestIndex]?.photo?.size || 'N/A'}
                            {guests[currentGuestIndex]?.photoType && `, Format: ${guests[currentGuestIndex].photoType}`}
                          </>
                        )}
                      </Typography>
                    </Alert>
                    <Button
                      variant="outlined"
                      onClick={() => setShowCamera(true)}
                      startIcon={<Refresh />}
                    >
                      {guests[currentGuestIndex]?.photoSource === 'loaded' 
                        ? 'Replace Photo' 
                        : t('retake_photo')
                      }
                    </Button>
                  </Box>
                )}
              </Grid>

              {/* Preferences Section - Only for first guest */}
              {currentGuestIndex === 0 && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      mt: 4, 
                      p: 3, 
                      bgcolor: 'grey.50', 
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}>
                      <Typography variant="h6" gutterBottom sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        color: 'text.primary',
                        mb: 3
                      }}>
                        <Info color="primary" />
                        {t('preferences')} <Chip label={t('optional')} size="small" variant="outlined" />
                      </Typography>
                      
                      {/* Aromatherapy Section */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                          🌸 {t('aromatherapy_question')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            variant={preferences.aromatherapy ? 'contained' : 'outlined'}
                            color={preferences.aromatherapy ? 'success' : 'inherit'}
                            onClick={() => setPreferences(prev => ({ ...prev, aromatherapy: true }))}
                            sx={{ minWidth: 80 }}
                          >
                            {t('yes')}
                          </Button>
                          <Button
                            variant={!preferences.aromatherapy ? 'contained' : 'outlined'}
                            color={!preferences.aromatherapy ? 'error' : 'inherit'}
                            onClick={() => setPreferences(prev => ({ ...prev, aromatherapy: false }))}
                            sx={{ minWidth: 80 }}
                          >
                            {t('no')}
                          </Button>
                        </Box>
                      </Box>

                      {/* Air Conditioning Section */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                          ❄️ {t('ac_question')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
                          <Button
                            variant={preferences.airConditioning ? 'contained' : 'outlined'}
                            color={preferences.airConditioning ? 'primary' : 'inherit'}
                            onClick={() => setPreferences(prev => ({ ...prev, airConditioning: true }))}
                            sx={{ minWidth: 80 }}
                          >
                            {t('yes')}
                          </Button>
                          <Button
                            variant={!preferences.airConditioning ? 'contained' : 'outlined'}
                            color={!preferences.airConditioning ? 'inherit' : 'inherit'}
                            onClick={() => setPreferences(prev => ({ ...prev, airConditioning: false }))}
                            sx={{ minWidth: 80 }}
                          >
                            {t('no')}
                          </Button>
                        </Box>
                        
                        {/* Temperature Control - Only show when AC is enabled */}
                        {preferences.airConditioning && (
                          <Box sx={{ 
                            mt: 2, 
                            p: 2, 
                            bgcolor: 'primary.50', 
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'primary.200'
                          }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              🌡️ {t('temperature')}
                            </Typography>
                            <TextField
                              type="number"
                              label={language === 'en' ? t('temperature_f') : t('temperature_c')}
                              value={language === 'en' ? celsiusToFahrenheit(preferences.temperature) : preferences.temperature}
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                
                                // Allow empty field for user to clear and retype
                                if (inputValue === '') {
                                  return;
                                }
                                
                                const value = parseFloat(inputValue);
                                
                                // Check if value is a valid number
                                if (isNaN(value)) {
                                  return;
                                }
                                
                                if (language === 'en') {
                                  // Convert F to C for storage
                                  const celsius = Math.round(fahrenheitToCelsius(value));
                                  // Allow any numeric input, but validate range
                                  if (value >= 61 && value <= 86) {
                                    setPreferences(prev => ({ ...prev, temperature: celsius }));
                                  }
                                } else {
                                  // Direct Celsius input
                                  const roundedValue = Math.round(value);
                                  // Allow any numeric input, but validate range
                                  if (value >= 16 && value <= 30) {
                                    setPreferences(prev => ({ ...prev, temperature: roundedValue }));
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                // On blur, ensure we have a valid value or reset to default
                                const inputValue = e.target.value;
                                if (inputValue === '' || isNaN(parseFloat(inputValue))) {
                                  setPreferences(prev => ({ ...prev, temperature: 22 }));
                                }
                              }}
                              inputProps={{
                                min: language === 'en' ? 61 : 16,
                                max: language === 'en' ? 86 : 30,
                                step: 1
                              }}
                              helperText={language === 'en' ? '61-86°F (16-30°C)' : '16-30°C (61-86°F)'}
                              size="small"
                              sx={{ maxWidth: 200 }}
                            />
                            
                            {/* Temperature increment/decrement buttons */}
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  const currentTemp = preferences.temperature;
                                  const newTemp = currentTemp - 1;
                                  if (newTemp >= 16 && newTemp <= 30) {
                                    setPreferences(prev => ({ ...prev, temperature: newTemp }));
                                  }
                                }}
                                disabled={preferences.temperature <= 16}
                                sx={{ minWidth: 40, height: 32 }}
                              >
                                -
                              </Button>
                              
                              <Typography variant="body2" sx={{ mx: 1, minWidth: 60, textAlign: 'center' }}>
                                {preferences.temperature}°C
                                {language === 'en' && ` (${celsiusToFahrenheit(preferences.temperature)}°F)`}
                              </Typography>
                              
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  const currentTemp = preferences.temperature;
                                  const newTemp = currentTemp + 1;
                                  if (newTemp >= 16 && newTemp <= 30) {
                                    setPreferences(prev => ({ ...prev, temperature: newTemp }));
                                  }
                                }}
                                disabled={preferences.temperature >= 30}
                                sx={{ minWidth: 40, height: 32 }}
                              >
                                +
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  disabled={currentGuestIndex === 0}
                  onClick={() => setCurrentGuestIndex(currentGuestIndex - 1)}
                >
                  {t('previous_guest')}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handlePartialSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : null}
                  data-save-button="true"
                >
                  {t('save_progress')}
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                {/* Submission Status Summary */}
                {(() => {
                  const { errors } = validateAndCompleteAllGuests();
                  const isReadyToSubmit = errors.length === 0;
                  
                  return (
                    <Box sx={{ textAlign: 'right', mb: 1 }}>
                      {isReadyToSubmit ? (
                        <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircle fontSize="small" />
                          Ready to submit!
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="warning.main" sx={{ fontSize: '0.75rem' }}>
                          {errors.length} {errors.length > 1 ? t('pending_guests') : t('pending_guest')}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
                
                <Button
                  variant="contained"
                  onClick={currentGuestIndex < guests.length - 1 ? handleNextGuest : handleFinalSubmit}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : null}
                >
                  {currentGuestIndex < guests.length - 1 ? t('next_guest') : t('submit_all')}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Camera Modal with react-camera-pro */}
      <Dialog
        open={showCamera}
        onClose={() => setShowCamera(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            minHeight: '650px',
            maxWidth: '600px'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CameraAlt />
            {t('take_photo')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', height: '500px' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Position your face in the center and click capture when ready.
            </Typography>
            
            <Box sx={{ 
              width: '100%',
              maxWidth: '500px',
              height: '400px',
              margin: '0 auto',
              border: '2px solid #ddd',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#000'
            }}>
              <Camera
                ref={cameraRef}
                aspectRatio={16/9}
                facingMode="user"
                videoSourceDeviceId={undefined}
                videoReadyCallback={() => {
                  console.log('Camera ready');
                }}
                numberOfCamerasCallback={(numberOfCameras) => {
                  console.log('Number of cameras:', numberOfCameras);
                }}
                errorMessages={{
                  noCameraAccessible: 'No camera device accessible. Please connect your camera or try a different browser.',
                  permissionDenied: 'Permission denied. Please refresh and give camera permission.',
                  switchCamera: 'It is not possible to switch camera to different one because there is only one video device accessible.',
                  canvas: 'Canvas is not supported.'
                }}
                videoConstraints={{
                  width: { ideal: 640, max: 1280 },
                  height: { ideal: 480, max: 720 },
                  facingMode: 'user'
                }}
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Make sure your entire face is visible in the frame
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCamera(false)}>
            {t('cancel')}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleTakePhoto}
            startIcon={<PhotoCamera />}
          >
            {t('capture')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Success Dialog */}
      <Dialog 
        open={showSaveSuccessDialog} 
        onClose={() => setShowSaveSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          {t('progress_saved')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('share_link_message')}
          </Typography>
          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.100', 
            borderRadius: 1, 
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            wordBreak: 'break-all'
          }}>
            {`${window.location.origin}/guest-info/${reservationId}`}
          </Box>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button 
            onClick={() => setShowSaveSuccessDialog(false)}
            variant="outlined"
          >
            {t('return_to_form')}
          </Button>
          <Button 
            onClick={copyLinkToClipboard}
            variant="contained"
            data-copy-button="true"
          >
            {t('copy_link')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GuestInformationFinal;
